import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, ChevronRight, UserPlus } from "lucide-react";
import api, { apiError } from "../../api";
import { useI18n } from "../../i18n";
import { useAuth } from "../../auth";
import { Button, Card, Input, Field, Select, Textarea, Modal, Badge, Spinner, EmptyState } from "../../components/ui";
import { toast } from "sonner";

export default function Patients() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const canClinical = ["professional", "clinic_admin", "superadmin"].includes(user.role);

  const load = () => api.get("/patients").then((r) => setPatients(r.data)).catch(() => setPatients([]));
  useEffect(() => { load(); }, []);

  const filtered = (patients || []).filter((p) =>
    `${p.first_name} ${p.last_name} ${p.internal_number} ${p.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-head font-bold text-navy text-2xl">{t("patients")}</h1>
          <p className="text-slate-500 text-sm">{(patients || []).length} dossier(s)</p>
        </div>
        <Button className="ms-auto" onClick={() => setOpen(true)} data-testid="new-patient-btn"><Plus size={17} /> {t("new_patient")}</Button>
      </div>

      <div className="relative max-w-md">
        <Search size={17} className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="ps-9" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="patient-search" />
      </div>

      {!patients ? <div className="flex justify-center py-16"><Spinner /></div> :
        filtered.length === 0 ? <EmptyState title={t("no_data")} hint={t("new_patient")} /> : (
        <Card className="divide-y divide-slate-100 overflow-hidden">
          {filtered.map((p) => (
            <button key={p.id} onClick={() => navigate(`/app/patients/${p.id}`)} data-testid={`patient-row-${p.id}`}
              className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-vlg transition text-start">
              <div className="h-10 w-10 rounded-full bg-teal-50 text-teal flex items-center justify-center font-head font-bold">
                {p.first_name[0]}{p.last_name[0]}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-navy">{p.first_name} {p.last_name}</div>
                <div className="text-xs text-slate-400">{p.internal_number} · {p.region || "—"}</div>
              </div>
              <div className="ms-auto flex items-center gap-3">
                {p.body_region && <Badge tone="navy">{p.body_region}</Badge>}
                <ChevronRight size={18} className="text-slate-300 rtl:rotate-180" />
              </div>
            </button>
          ))}
        </Card>
      )}

      <NewPatientModal open={open} onClose={() => setOpen(false)} onDone={load} t={t} canClinical={canClinical} />
    </div>
  );
}

function NewPatientModal({ open, onClose, onDone, t, canClinical }) {
  const empty = { first_name: "", last_name: "", birth_date: "", phone: "", email: "", address: "",
    region: "", lang: "fr", body_region: "", reason: "", diagnosis: "", precautions: "",
    functional_goals: "", initial_pain: 0, create_portal_access: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!f.first_name || !f.last_name) { toast.error("Prénom et nom requis"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/patients", f);
      toast.success("Patient créé");
      if (data.portal_access) toast.info(`Accès portail : ${data.portal_access.email} / ${data.portal_access.temp_password}`, { duration: 10000 });
      setF(empty); onClose(); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={t("new_patient")} size="lg">
      <div className="space-y-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-teal mb-2">{t("identity")}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={t("first_name")}><Input data-testid="np-first" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
            <Field label={t("last_name")}><Input data-testid="np-last" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
            <Field label={t("birth_date")}><Input type="date" value={f.birth_date} onChange={(e) => set("birth_date", e.target.value)} /></Field>
            <Field label={t("phone")}><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label={t("email")}><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label={t("region")}><Input value={f.region} onChange={(e) => set("region", e.target.value)} /></Field>
            <Field label={t("lang")}>
              <Select value={f.lang} onChange={(e) => set("lang", e.target.value)}>
                <option value="fr">Français</option><option value="ar">العربية</option><option value="en">English</option>
              </Select>
            </Field>
            <Field label={t("address")}><Input value={f.address} onChange={(e) => set("address", e.target.value)} /></Field>
          </div>
        </div>

        {canClinical && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-teal mb-2">{t("clinical_context")}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Région anatomique">
                <Select value={f.body_region} onChange={(e) => set("body_region", e.target.value)}>
                  <option value="">—</option><option value="cervicale">Cervicale</option>
                  <option value="thoracique">Thoracique</option><option value="lombaire">Lombaire</option><option value="hanche">Hanche</option><option value="autre">Autre</option>
                </Select>
              </Field>
              <Field label={`${t("difficulty_level")} initiale (0-10)`}><Input type="number" min="0" max="10" value={f.initial_pain} onChange={(e) => set("initial_pain", Number(e.target.value))} /></Field>
              <div className="sm:col-span-2"><Field label={t("reason")}><Input value={f.reason} onChange={(e) => set("reason", e.target.value)} /></Field></div>
              <div className="sm:col-span-2"><Field label={t("diagnosis")}><Input data-testid="np-diagnosis" value={f.diagnosis} onChange={(e) => set("diagnosis", e.target.value)} /></Field></div>
              <div className="sm:col-span-2"><Field label={t("precautions")}><Textarea rows={2} value={f.precautions} onChange={(e) => set("precautions", e.target.value)} /></Field></div>
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={f.create_portal_access} onChange={(e) => set("create_portal_access", e.target.checked)} className="range-teal" data-testid="np-portal" />
          Créer un accès portail patient (nécessite un courriel)
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={submit} disabled={busy} data-testid="np-submit">{busy ? t("loading") : t("create")}</Button>
        </div>
      </div>
    </Modal>
  );
}
